import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, Button, Badge } from "reactstrap";
import {
  Calendar,
  Clock,
  AlertTriangle,
  AlertOctagon,
  AlertCircle,
  X,
  ArrowRight,
  RefreshCw,
  Award,
  FileText,
  CheckCircle,
} from "react-feather";
import axios from "axios";

const API_URL = process.env.REACT_APP_BASEURL || "http://localhost:8000/api/";

const getToken = () => {
  return localStorage.getItem(process.env.REACT_APP_USERTOKEN);
};

/**
 * Reusable Subscription Expiry Banner
 *
 * @param {Object} props
 * @param {Object} props.planData - Current plan data from API
 * @param {string} props.mode - "dashboard" | "manage_subscription"
 * @param {Function} [props.onUpgradeClick] - Optional callback to open the Annual upgrade modal
 * @param {Function} [props.onRequestInvoice] - Optional callback to open the Request Invoice modal
 */
const SubscriptionExpiryBanner = ({
  planData,
  mode = "dashboard",
  onUpgradeClick,
  onRequestInvoice,
}) => {
  const plan = planData?.data || planData || {};
  const {
    expiry_date,
    days_until_renewal,
    is_cancelled,
    is_expire,
    billing_status,
    plan: planName,
    subscription_type,
    upgrade_eligible,
    upgrade_discount_percent,
    renewal_prompt_dismissed,
    invoice_renewal,
    is_suspended,
    is_in_retry_grace,
    retry_attempt,
    retry_max_attempts,
    retry_grace_days_remaining,
    next_retry_at,
  } = plan;

  const isInFinal10Days = (days_until_renewal !== null && days_until_renewal !== undefined && days_until_renewal <= 10 && days_until_renewal >= 0 && !is_expire);
  const todayDateStr = new Date().toISOString().slice(0, 10);
  const dismissalScope = `${plan.current_plan_key || planName || "plan"}_${expiry_date || "unknown"}`;

  // Compute current milestone (45, 30, 15, 10, 5, 0)
  const computeMilestone = (days, expired, suspended, retryGrace) => {
    if (suspended) return -99; // Suspended
    if (retryGrace) return -98; // Payment retry grace
    if (expired) {
      return 0; // Expired
    }
    if (days === null || days === undefined) return null;
    if (days <= 5) return 5;
    if (days <= 10) return 10;
    if (days <= 15) return 15;
    if (days <= 30) return 30;
    if (days <= 45) return 45;
    return null; // More than 45 days, banner not needed
  };

  const currentMilestone = computeMilestone(
    days_until_renewal,
    is_expire,
    is_suspended,
    is_in_retry_grace,
  );

  // Dismissal state
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (is_suspended || is_in_retry_grace) {
      // Payment failure and suspension banners can never be dismissed.
      setIsDismissed(false);
      return;
    }

    if (mode === "dashboard" && currentMilestone !== null) {
      if (isInFinal10Days) {
        // In final 10 days: banner shows EVERY DAY. Check daily dismissal key
        const dailyDismissed = localStorage.getItem(`dismissed_expiry_daily_${dismissalScope}_${todayDateStr}`);
        setIsDismissed(Boolean(dailyDismissed));
      } else {
        const storedDismissedMilestone = localStorage.getItem(`dismissed_expiry_milestone_${dismissalScope}`);
        const backendDismissed =
          renewal_prompt_dismissed !== undefined &&
          renewal_prompt_dismissed !== null &&
          Number(renewal_prompt_dismissed) === currentMilestone;

        if (storedDismissedMilestone === String(currentMilestone) || backendDismissed) {
          setIsDismissed(true);
        } else {
          setIsDismissed(false);
        }
      }
    } else {
      setIsDismissed(false);
    }
  }, [currentMilestone, renewal_prompt_dismissed, mode, isInFinal10Days, is_suspended, is_in_retry_grace]);

  const handleDismiss = async () => {
    setIsDismissed(true);
    if (isInFinal10Days) {
      // In final 10 days, only dismiss for today (reappears tomorrow)
      localStorage.setItem(`dismissed_expiry_daily_${dismissalScope}_${todayDateStr}`, "true");
    } else if (currentMilestone !== null) {
      localStorage.setItem(`dismissed_expiry_milestone_${dismissalScope}`, String(currentMilestone));
      try {
        await axios.post(
          `${API_URL}brand/dismiss-expiry-banner`,
          { milestone: currentMilestone },
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );
      } catch (e) {}
    }
  };

  const upcomingSub = plan.upcoming_subscription;
  const hasUpcomingPaid = Boolean(upcomingSub);

  // If no milestone reached and no upcoming sub, or dismissed on dashboard, don't render
  if (currentMilestone === null && !hasUpcomingPaid) return null;
  if (mode === "dashboard" && isDismissed && !is_suspended && !is_in_retry_grace && !hasUpcomingPaid) return null;

  // Determine styling based on milestone urgency and renewal mode
  let accentBorderColor = "#7367F0";
  let iconBgColor = "rgba(115, 103, 240, 0.1)";
  let iconComponent = <Clock size={18} className="text-primary" />;
  let badgeColor = "light-primary";
  let badgeText = invoice_renewal ? "Invoice Renewal" : "Auto-Renewal";
  let title = "Subscription Renewal Approaching";
  let descriptionText = `Your subscription cycle will end on ${expiry_date || "the scheduled date"}.`;

  if (hasUpcomingPaid) {
    accentBorderColor = "#28C76F";
    iconBgColor = "rgba(40, 199, 111, 0.12)";
    iconComponent = <CheckCircle size={18} className="text-success" />;
    badgeColor = "light-success";
    badgeText = "Renewal Scheduled";
    title = "Upcoming Renewal Paid & Scheduled";
    descriptionText = `Your current plan is active through ${expiry_date}. ${upcomingSub.plan_name} begins automatically on ${upcomingSub.starts_at}.`;
  } else if (is_suspended) {
    accentBorderColor = "#EA5455";
    iconBgColor = "rgba(234, 84, 85, 0.12)";
    iconComponent = <AlertOctagon size={18} className="text-danger" />;
    badgeColor = "danger";
    badgeText = "Action Required";
    title = "Account Suspended Due to Unpaid Renewal";
    descriptionText = "Your account access is currently paused. Pay your renewal invoice to restore immediate campaign and dietitian access.";
  } else if (is_in_retry_grace) {
    accentBorderColor = retry_attempt >= 2 ? "#EA5455" : "#FF9F43";
    iconBgColor = retry_attempt >= 2
      ? "rgba(234, 84, 85, 0.12)"
      : "rgba(255, 159, 67, 0.12)";
    iconComponent = retry_attempt >= 2
      ? <AlertOctagon size={18} className="text-danger" />
      : <AlertTriangle size={18} className="text-warning" />;
    badgeColor = retry_attempt >= 2 ? "light-danger" : "light-warning";
    badgeText = retry_attempt > 0
      ? `Retry ${retry_attempt} of ${retry_max_attempts || 3} Failed`
      : "Renewal Charge Failed";
    title = retry_attempt >= 2
      ? "Final Payment Retry Remaining"
      : "Automatic Renewal Payment Failed";
    descriptionText = `Your account remains fully active during the payment grace period. ${retry_grace_days_remaining || 0} days remain before suspension${next_retry_at ? `; the next retry is scheduled for ${next_retry_at}` : ""}. Update billing or pay by invoice to avoid interruption.`;
  } else if (currentMilestone === 0) {
    accentBorderColor = "#EA5455";
    iconBgColor = "rgba(234, 84, 85, 0.12)";
    iconComponent = <AlertCircle size={18} className="text-danger" />;
    badgeColor = "light-danger";
    badgeText = "Expired";
    title = "Subscription Expired";
    descriptionText = `Your subscription cycle ended on ${expiry_date || "recently"}. Request an invoice or renew to resume active outreach.`;
  } else if (currentMilestone === 5 || currentMilestone === 10) {
    accentBorderColor = "#EA5455";
    iconBgColor = "rgba(234, 84, 85, 0.12)";
    iconComponent = <AlertOctagon size={18} className="text-danger" />;
    badgeColor = "light-danger";
    badgeText = days_until_renewal <= 0 ? "Ends Today" : `${days_until_renewal} Days Left`;
    title = invoice_renewal
      ? (days_until_renewal <= 0 ? "Renewal Invoice Due Today" : `Renewal Invoice Due in ${days_until_renewal} Days`)
      : (days_until_renewal <= 0 ? "Card Auto-Renews Today" : `Card Auto-Renews in ${days_until_renewal} Days`);
    descriptionText = invoice_renewal
      ? `Your renewal invoice is due on ${expiry_date}. Complete payment online to maintain uninterrupted campaign access.`
      : `Your payment method on file will be charged automatically on ${expiry_date}. You can switch to invoice payment or update your card.`;
  } else if (currentMilestone === 15) {
    accentBorderColor = "#FF9F43";
    iconBgColor = "rgba(255, 159, 67, 0.12)";
    iconComponent = <AlertTriangle size={18} className="text-warning" />;
    badgeColor = "light-warning";
    badgeText = "15 Days Left";
    title = "Subscription Renews in 15 Days";
    descriptionText = `Your current subscription period concludes on ${expiry_date}. Pay early by corporate invoice (Net 14) or review your renewal plan.`;
  } else if (currentMilestone === 30) {
    accentBorderColor = "#7367F0";
    iconBgColor = "rgba(115, 103, 240, 0.1)";
    iconComponent = <Clock size={18} className="text-primary" />;
    badgeColor = "light-primary";
    badgeText = "30 Days Left";
    title = "Subscription Renews in 30 Days";
    descriptionText = `Your ${planName ? `${planName} ` : ""}subscription cycle ends on ${expiry_date}. You can pay by corporate invoice (Net 14) or explore annual upgrade savings.`;
  } else if (currentMilestone === 45) {
    accentBorderColor = "#00CFE8";
    iconBgColor = "rgba(0, 207, 232, 0.12)";
    iconComponent = <Calendar size={18} className="text-info" />;
    badgeColor = "light-info";
    badgeText = "45 Days Notice";
    title = "Subscription Renews in 45 Days";
    descriptionText = `Your subscription period will renew on ${expiry_date}. Early renewal and annual discount options are available.`;
  }

  // Determine billing status description
  const isCancelledSub = is_cancelled || billing_status === "cancelled";
  const isExpiredSub = !is_in_retry_grace && (is_expire || billing_status === "expired" || is_suspended);

  return (
    <Card
      className="shadow-sm mb-2 subscription-expiry-banner border-0"
      style={{
        borderRadius: "10px",
        borderLeft: `4px solid ${accentBorderColor}`,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 2px 10px 0 rgba(34, 41, 47, 0.05)",
      }}
    >
      <CardBody className="py-1 px-1 px-sm-1">
        <div className="d-flex align-items-center justify-content-between flex-wrap flex-lg-nowrap">
          {/* Left: Icon & Notification Text */}
          <div className="d-flex align-items-center mr-1 mb-50 mb-lg-0" style={{ minWidth: 0, flex: 1 }}>
            <div
              className="d-flex align-items-center justify-content-center mr-1 flex-shrink-0"
              style={{
                width: "38px",
                height: "38px",
                backgroundColor: iconBgColor,
                borderRadius: "8px",
              }}
            >
              {iconComponent}
            </div>

            <div style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center flex-wrap">
                <span className="font-weight-bolder text-dark mr-50 font-medium-1">
                  {title}
                </span>
                <Badge color={badgeColor} pill className="font-small-1 font-weight-bold px-75 py-25 mr-25">
                  {badgeText}
                </Badge>
                {invoice_renewal && !hasUpcomingPaid && (
                  <Badge color="light-info" pill className="font-small-1 px-50 py-25">
                    Net 14 Invoice
                  </Badge>
                )}
              </div>

              <div className="text-muted font-small-2 mt-25" style={{ lineHeight: "1.45" }}>
                {descriptionText}
              </div>
            </div>
          </div>

          {/* Right: Actions Group */}
          <div className="d-flex align-items-center ml-lg-auto flex-wrap flex-sm-nowrap flex-shrink-0 mt-50 mt-lg-0">
            {hasUpcomingPaid ? (
              onRequestInvoice && (
                <Button
                  color="success"
                  size="sm"
                  className="mr-50 font-weight-bold d-inline-flex align-items-center shadow-none px-1"
                  onClick={onRequestInvoice}
                >
                  <CheckCircle size={14} className="mr-50" />
                  View Renewal Details
                </Button>
              )
            ) : (
              <>
                {upgrade_eligible && onUpgradeClick && (
                  <Button
                    color="flat-warning"
                    size="sm"
                    className="mr-50 font-weight-bold d-inline-flex align-items-center px-75"
                    onClick={onUpgradeClick}
                  >
                    <Award size={14} className="mr-50 text-warning" />
                    {Number(upgrade_discount_percent) > 0
                      ? `Upgrade & Save ${upgrade_discount_percent}%`
                      : "Upgrade to Annual"}
                  </Button>
                )}

                {onRequestInvoice && (
                  <Button
                    color="primary"
                    size="sm"
                    className="mr-50 font-weight-bold d-inline-flex align-items-center shadow-sm px-1"
                    onClick={onRequestInvoice}
                  >
                    <FileText size={14} className="mr-50" />
                    Pay by Invoice
                  </Button>
                )}
              </>
            )}

            {mode === "dashboard" ? (
              <Button
                tag={Link}
                to="/dashboard/brands/subscription-plan"
                color="flat-primary"
                size="sm"
                className="font-weight-bold d-inline-flex align-items-center mr-25 px-75"
              >
                Manage
                <ArrowRight size={14} className="ml-25" />
              </Button>
            ) : (
              (isCancelledSub || isExpiredSub) && !hasUpcomingPaid && !is_in_retry_grace && (
                <Button
                  tag={Link}
                  to="/subscription-plans"
                  color="primary"
                  size="sm"
                  className="font-weight-bold d-inline-flex align-items-center mr-50 shadow-sm px-1"
                >
                  <RefreshCw size={14} className="mr-50" />
                  Reactivate Plan
                </Button>
              )
            )}

            {mode === "dashboard" && !hasUpcomingPaid && !is_in_retry_grace && (
              <Button
                color="flat-secondary"
                size="sm"
                className="btn-icon p-25 text-muted ml-25"
                onClick={handleDismiss}
                title="Dismiss notification"
                aria-label="Dismiss notification"
              >
                <X size={16} />
              </Button>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default SubscriptionExpiryBanner;
